import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "../components/DirectionalIcon";
import { buildLocalizedPath } from "../constants/languages";

// Panel brand data with logos
const panelBrands = [
  {
    name: "Canadian Solar",
    country: "Canada",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6c/Canadian_Solar_logo.svg/320px-Canadian_Solar_logo.svg.png",
    invert: true,
    descriptionKey: "panelsWeTrade.brands.canadianSolar.description",
  },
  {
    name: "Hanwha Q Cells",
    country: "South Korea / Germany",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Hanwha_Q_CELLS_logo.svg/320px-Hanwha_Q_CELLS_logo.svg.png",
    invert: true,
    descriptionKey: "panelsWeTrade.brands.hanwhaQCells.description",
  },
  {
    name: "Hansol",
    country: "South Korea",
    logo: "https://magalaxyintl.com/wp-content/uploads/2023/01/hansol.png",
    invert: false,
    descriptionKey: "panelsWeTrade.brands.hansol.description",
  },
  {
    name: "JA Solar",
    country: "China",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a3/JA_Solar_logo.svg/320px-JA_Solar_logo.svg.png",
    invert: true,
    descriptionKey: "panelsWeTrade.brands.jaSolar.description",
  },
  {
    name: "Jinko Solar",
    country: "China",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Jinko_Solar_logo.svg/320px-Jinko_Solar_logo.svg.png",
    invert: true,
    descriptionKey: "panelsWeTrade.brands.jinkoSolar.description",
  },
  {
    name: "LG Solar",
    country: "South Korea",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bf/LG_logo_%282015%29.svg/320px-LG_logo_%282015%29.svg.png",
    invert: true,
    descriptionKey: "panelsWeTrade.brands.lgSolar.description",
  },
  {
    name: "LONGi Solar",
    country: "China",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/LONGi_Solar_logo.svg/320px-LONGi_Solar_logo.svg.png",
    invert: true,
    descriptionKey: "panelsWeTrade.brands.longiSolar.description",
  },
  {
    name: "Risen Energy",
    country: "China",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Risen_Energy_logo.svg/320px-Risen_Energy_logo.svg.png",
    invert: true,
    descriptionKey: "panelsWeTrade.brands.risenEnergy.description",
  },
  {
    name: "S-Energy",
    country: "South Korea",
    logo: "https://magalaxyintl.com/wp-content/uploads/2023/01/s-energy.png",
    invert: false,
    descriptionKey: "panelsWeTrade.brands.sEnergy.description",
  },
  {
    name: "Shinsung E&G",
    country: "South Korea",
    logo: "https://magalaxyintl.com/wp-content/uploads/2023/01/e-and-g.png",
    invert: false,
    descriptionKey: "panelsWeTrade.brands.shinsungEG.description",
  },
  {
    name: "SolarPark",
    country: "South Korea",
    logo: "https://magalaxyintl.com/wp-content/uploads/2023/01/solar-park.png",
    invert: false,
    descriptionKey: "panelsWeTrade.brands.solarPark.description",
  },
  {
    name: "Trina Solar",
    country: "China",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6e/Trina_Solar_logo.svg/320px-Trina_Solar_logo.svg.png",
    invert: true,
    descriptionKey: "panelsWeTrade.brands.trinaSolar.description",
  },
];

const allBrandNames = [
  "Hanwha Q Cells",
  "JA Solar",
  "Wuxi Suntech Power",
  "Trina Solar",
  "S-Energy",
  "Astronergy / Chint Solar",
  "Risen Energy",
  "Jinko Solar",
  "Shinsung E&G",
  "LONGi Solar",
  "GCL System Integration",
  "LG Electronics Solar",
  "Hansol Technics",
  "Suzhou Talesun Solar",
  "SolarPark Korea",
  "Canadian Solar",
];

const BrandCard = ({ brand, index }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="bg-slate-800/50 border border-slate-700 p-6 rounded-xl hover:border-gold/30 transition-all group"
    >
      {/* Logo */}
      <div className="h-12 mb-4 flex items-center justify-center">
        <img
          src={brand.logo}
          alt={`${brand.name} logo`}
          className={`h-12 max-w-[140px] object-contain ${brand.invert ? 'brightness-0 invert' : ''}`}
          loading="lazy"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <span
          className="text-2xl font-outfit font-bold text-gold hidden"
        >
          {brand.name}
        </span>
      </div>

      {/* Name & Country */}
      <h3 className="font-outfit text-lg font-semibold text-white mb-1 group-hover:text-gold transition-colors">
        {brand.name}
      </h3>
      <p className="text-gold text-sm mb-3">{brand.country}</p>

      {/* Description */}
      <p className="text-slate-400 text-sm leading-relaxed">
        {t(brand.descriptionKey)}
      </p>
    </motion.div>
  );
};

const PanelsWeTradePage = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.title = t("panelsWeTrade.meta.title");
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("panelsWeTrade.meta.description"));
    }
  }, [t]);

  return (
    <div data-testid="panels-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 bg-navy text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">
              {t("panelsWeTrade.hero.kicker")}
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold mt-4 mb-6">
              {t("panelsWeTrade.hero.title")} <span className="text-gold">{t("panelsWeTrade.hero.titleAccent")}</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl">
              {t("panelsWeTrade.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Brand Marquee */}
      <section className="py-12 bg-slate-900 overflow-hidden border-b border-slate-800">
        <div className="flex whitespace-nowrap">
          <div className="brand-track">
            {[...allBrandNames, ...allBrandNames].map((brand, index) => (
              <span
                key={`${brand}-${index}`}
                className="text-slate-500 text-sm uppercase tracking-wider font-medium"
              >
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Main Brands Grid */}
      <section className="py-20 md:py-32 bg-slate-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("panelsWeTrade.brandsGrid.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-4">
              {t("panelsWeTrade.brandsGrid.title")}
            </h2>
            <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
              {t("panelsWeTrade.brandsGrid.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {panelBrands.map((brand, index) => (
              <BrandCard key={brand.name} brand={brand} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Full Brand List */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
                {t("panelsWeTrade.fullBrandList.kicker")}
              </span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4 mb-6">
                {t("panelsWeTrade.fullBrandList.title")}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                {t("panelsWeTrade.fullBrandList.description")}
              </p>
              <Link
                to={buildLocalizedPath("/contact", i18n.language)}
                data-testid="panels-enquire-btn"
                className="inline-flex items-center gap-2 bg-navy text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-navy-900 transition-colors"
              >
                {t("panelsWeTrade.fullBrandList.enquireButton")}
                <ChevronRight size={16} />
              </Link>
            </div>

            <div className="bg-white border border-slate-200 p-8">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {allBrandNames.map((brand, index) => (
                  <li
                    key={brand}
                    className="flex items-center gap-3 text-slate-600"
                  >
                    <span className="w-2 h-2 bg-gold rounded-full flex-shrink-0" />
                    {brand}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Any Brand Section */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="bg-navy text-white p-8 md:p-16 text-center">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-6">
              {t("panelsWeTrade.anyBrand.title")}
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              {t("panelsWeTrade.anyBrand.description")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={buildLocalizedPath("/sell-panels", i18n.language)}
                data-testid="panels-sell-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
              >
                {t("panelsWeTrade.anyBrand.sellButton")}
                <ChevronRight size={16} />
              </Link>
              <Link
                to={buildLocalizedPath("/buy-panels", i18n.language)}
                data-testid="panels-buy-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-white/5 transition-colors"
              >
                {t("panelsWeTrade.anyBrand.buyButton")}
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How to Buy Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("panelsWeTrade.howToBuy.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4">
              {t("panelsWeTrade.howToBuy.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Retail Column */}
            <div className="bg-white border border-slate-200 p-8">
              <span className="inline-block px-3 py-1 bg-gold/20 text-gold text-xs font-bold uppercase tracking-wider mb-4">
                {t("panelsWeTrade.howToBuy.retail.badge")}
              </span>
              <h3 className="font-outfit text-xl font-semibold text-navy mb-4">
                {t("panelsWeTrade.howToBuy.retail.title")}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {t("panelsWeTrade.howToBuy.retail.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://ebay.us/m/XZWRVV"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="how-to-buy-ebay"
                  className="inline-flex items-center justify-center gap-2 bg-navy text-white px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-navy-900 transition-colors"
                >
                  {t("panelsWeTrade.howToBuy.retail.ebayButton")}
                  <ChevronRight size={14} />
                </a>
                <a
                  href="https://www.facebook.com/marketplace/profile/61577288068819/?ref=permalink"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="how-to-buy-facebook"
                  className="inline-flex items-center justify-center gap-2 border border-slate-300 text-navy px-6 py-3 text-xs font-bold uppercase tracking-widest hover:border-navy transition-colors"
                >
                  {t("panelsWeTrade.howToBuy.retail.facebookButton")}
                  <ChevronRight size={14} />
                </a>
              </div>
            </div>

            {/* Bulk Column */}
            <div className="bg-white border border-slate-200 p-8">
              <span className="inline-block px-3 py-1 bg-gold/20 text-gold text-xs font-bold uppercase tracking-wider mb-4">
                {t("panelsWeTrade.howToBuy.bulk.badge")}
              </span>
              <h3 className="font-outfit text-xl font-semibold text-navy mb-4">
                {t("panelsWeTrade.howToBuy.bulk.title")}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {t("panelsWeTrade.howToBuy.bulk.description")}
              </p>
              <Link
                to={buildLocalizedPath("/buy-panels#bulk", i18n.language)}
                data-testid="how-to-buy-bulk"
                className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
              >
                {t("panelsWeTrade.howToBuy.bulk.button")}
                <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PanelsWeTradePage;
